import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Trash2, Check, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, formatDate, formatMoney } from "@/lib/api";

// Detail modal for the "Pending to clear" drill-in. Similar to
// BillDetailModal but shows a mixed list of bills + freeform pending
// charges (from /api/pending) that reduce a specific source's balance.
export function PendingDetailModal({ sourceName, sourceId, items, total, onClose, onItemRemoved, onItemAdded, onBillCleared, testId, showSourceName = false, hideAddForm = false }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const [busyId, setBusyId] = useState(null);
  const [addName, setAddName] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [adding, setAdding] = useState(false);

  const clearBill = async (item) => {
    if (item.kind !== "bill") return;
    setBusyId(item.id);
    try {
      await api.patch(`/bills/${item.id}`, { status: "cleared", paid_amount: Number(item.amount || 0) });
      toast.success(`Marked "${item.name}" as Cleared`);
      onBillCleared && onBillCleared(item);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusyId(null); }
  };

  const addPending = async (e) => {
    e.preventDefault();
    const amt = Number(addAmount);
    if (!addName.trim() || !amt || amt <= 0) return;
    setAdding(true);
    try {
      const { data } = await api.post("/pending-payments", {
        description: addName.trim(),
        amount: amt,
        funding_source_id: sourceId || undefined,
      });
      toast.success(`Added "${data.description}" to pending`);
      setAddName(""); setAddAmount("");
      onItemAdded && onItemAdded(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setAdding(false); }
  };

  const remove = async (item) => {
    if (item.kind !== "pending") return; // Bills are managed on the Bills page
    setBusyId(item.id);
    try {
      // Snapshot the row so we can Undo — if the user tapped by accident,
      // one click on "Undo" restores it. No native confirm dialog needed;
      // that just made the trash icon feel unresponsive on Cancel.
      const snapshot = {
        description: item.name,
        amount: Number(item.amount || 0),
        funding_source_id: item.funding_source_id || undefined,
      };
      await api.delete(`/pending-payments/${item.id}`);
      onItemRemoved && onItemRemoved(item);
      toast.success(`Removed "${item.name}" from pending`, {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post("/pending-payments", snapshot);
              toast.success(`Restored "${item.name}"`);
              // Nudge parent to re-fetch so the row reappears in the modal.
              onItemRemoved && onItemRemoved({ ...item, __undo: true });
            } catch (e) {
              toast.error(`Undo failed: ${formatApiError(e)}`);
            }
          },
        },
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const body = (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={`${testId}-backdrop`}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl p-6 max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="font-heading text-xl text-[#2C302E]">Pending to clear</h3>
            <p className="text-xs text-[#59605C] mt-1 leading-relaxed">
              {showSourceName ? (
                <>Bills and manual charges across <span className="font-semibold">all your accounts</span> that have left the ledger but haven&apos;t cleared the bank yet. Once each one clears, mark it Paid + Cleared. Each row shows which account it&rsquo;s tied to.</>
              ) : (
                <>Charges linked to <span className="font-semibold">{sourceName}</span> that
                have left the ledger but haven&apos;t cleared the bank yet. Once they clear,
                mark them Paid + Cleared on the bill sheet. To remove a manual placeholder
                pending charge, click the trash icon on the right (you&apos;ll get an Undo option).</>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-[#8A918D] hover:text-[#2C302E] shrink-0"
            aria-label="Close"
            data-testid={`${testId}-close`}
          >
            <X size={20} />
          </button>
        </div>
        <div className="overflow-y-auto -mx-6 px-6 divide-y divide-[#EBE6E0] border-y border-[#EBE6E0]">
          {items.length === 0 ? (
            <div className="py-6 text-sm text-[#8A918D] italic">No pending items to show.</div>
          ) : items.map((it, idx) => (
            <div
              key={`${it.kind}-${it.id || idx}`}
              className="py-3 flex items-center justify-between gap-3"
              data-testid={`${testId}-item-${it.id || idx}`}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#2C302E] truncate">{it.name}</div>
                <div className="text-xs text-[#8A918D] mt-0.5">
                  {it.date ? `Due ${formatDate(it.date)}` : "Manual pending charge"}
                  {it.is_autopay && <span className="ml-2 text-[#4A6148]">· autopay</span>}
                  <span className="ml-2 uppercase text-[10px] tracking-widest text-[#8A918D]">
                    {it.kind === "bill" ? "· bill" : "· pending charge"}
                  </span>
                  {showSourceName && it.sourceName && (
                    <span className="ml-2 text-[10px] font-semibold uppercase tracking-widest text-[#4A6148]">
                      · {it.sourceName}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-sm font-semibold text-[#7A2E24] whitespace-nowrap">
                  {formatMoney(it.amount)}
                </div>
                {it.kind === "pending" && (
                  <button
                    type="button"
                    onClick={() => remove(it)}
                    disabled={busyId === it.id}
                    aria-label={`Remove ${it.name}`}
                    className="p-1.5 rounded-md text-[#8A918D] hover:text-[#7A2E24] hover:bg-[#F5D9D1] transition-colors disabled:opacity-50"
                    data-testid={`${testId}-remove-${it.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
                {it.kind === "bill" && (
                  <button
                    type="button"
                    onClick={() => clearBill(it)}
                    disabled={busyId === it.id}
                    aria-label={`Mark ${it.name} as cleared`}
                    title="Mark this bill as Cleared — it hit the bank"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#4A6148] text-white hover:bg-[#3E5240] transition-colors disabled:opacity-50 shadow-sm"
                    data-testid={`${testId}-clear-${it.id}`}
                  >
                    <Check size={13} />
                    {busyId === it.id ? "Clearing…" : "Mark cleared"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        {!hideAddForm && (
          <form
            className="pt-3 flex items-center gap-2 border-b border-[#EBE6E0] pb-3"
            onSubmit={addPending}
            data-testid={`${testId}-add-form`}
          >
            <input
              type="text"
              placeholder="e.g., Grocery card swipe"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              className="flex-1 min-w-0 px-3 py-1.5 text-sm rounded-lg border border-[#CDC7BB] bg-white focus:outline-none focus:border-[#4A6148]"
              data-testid={`${testId}-add-name`}
            />
            <input
              type="number" step="0.01" placeholder="$"
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              className="w-24 px-3 py-1.5 text-sm rounded-lg border border-[#CDC7BB] bg-white focus:outline-none focus:border-[#4A6148]"
              data-testid={`${testId}-add-amount`}
            />
            <button
              type="submit"
              disabled={adding || !addName.trim() || !Number(addAmount)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#4A6148] text-white hover:bg-[#3E5240] disabled:opacity-50"
              data-testid={`${testId}-add-submit`}
            >
              <Plus size={13} /> Add
            </button>
          </form>
        )}
        <div className="pt-3 flex items-center justify-between">
          <div className="text-sm font-semibold text-[#59605C]">Total pending</div>
          <div className="text-lg font-heading font-bold text-[#7A2E24]">
            {formatMoney(total)}
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(body, document.body) : body;
}
